import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@/providers/DialogProvider';
import {
  parseAlignments,
  parseBreaklines,
  parseParcels,
  parsePipeNetworks,
  parseSurfaces,
  type LandXMLFeature,
} from '@/core/landxml';
import { Button } from './ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

interface Props {
  layerName: string;
  fileUrl: string;
}

interface Summary {
  surfaceCount: number;
  surfaceArea: number;
  alignmentCount: number;
  alignmentLength: number;
  parcelCount: number;
  parcelArea: number;
  breaklineCount: number;
  breaklineLength: number;
  pipeCount: number;
  pipeLength: number;
  pipesByDiameter: Map<string, { count: number; length: number }>;
  structureCount: number;
}

const EMPTY: Summary = {
  surfaceCount: 0,
  surfaceArea: 0,
  alignmentCount: 0,
  alignmentLength: 0,
  parcelCount: 0,
  parcelArea: 0,
  breaklineCount: 0,
  breaklineLength: 0,
  pipeCount: 0,
  pipeLength: 0,
  pipesByDiameter: new Map(),
  structureCount: 0,
};

function summarize(
  surfaces: LandXMLFeature[],
  alignments: LandXMLFeature[],
  parcels: LandXMLFeature[],
  breaklines: LandXMLFeature[],
  pipeMeshes: LandXMLFeature[],
  pipeLines: LandXMLFeature[],
): Summary {
  const out: Summary = { ...EMPTY, pipesByDiameter: new Map() };
  for (const s of surfaces) {
    out.surfaceCount++;
    out.surfaceArea += s.metadata.surfaceArea ?? 0;
  }
  for (const a of alignments) {
    out.alignmentCount++;
    out.alignmentLength += a.metadata.length ?? 0;
  }
  for (const p of parcels) {
    out.parcelCount++;
    out.parcelArea += p.metadata.parcelArea ?? 0;
  }
  for (const b of breaklines) {
    out.breaklineCount++;
    out.breaklineLength += b.metadata.length ?? 0;
  }
  // Pipes: count + length come from the centerline list (one per pipe);
  // structure mesh count is the structure total. The mesh list also has
  // PIPE-category entries (the cylinders) — skip those for length so we
  // don't double-count.
  for (const m of pipeMeshes) {
    if (m.category === 'STRUCTURE') out.structureCount++;
  }
  for (const l of pipeLines) {
    if (l.category !== 'PIPE') continue;
    out.pipeCount++;
    const len = l.metadata.length ?? 0;
    out.pipeLength += len;
    const d = l.metadata.diameter;
    if (d !== undefined) {
      const key = `${(d * 1000).toFixed(0)} mm`;
      const entry = out.pipesByDiameter.get(key) ?? { count: 0, length: 0 };
      entry.count++;
      entry.length += len;
      out.pipesByDiameter.set(key, entry);
    }
  }
  return out;
}

export const LandXMLQuantitiesDialog: FunctionComponent<Props> = ({ layerName, fileUrl }) => {
  const { t } = useTranslation('components');
  const dialog = useDialog();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<Summary>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(fileUrl, { credentials: 'include' });
        const text = await response.text();
        if (cancelled) return;
        const doc = new DOMParser().parseFromString(text, 'application/xml');
        const parserError = doc.querySelector('parsererror');
        if (parserError) {
          setError(parserError.textContent ?? 'Parse error');
          setLoading(false);
          return;
        }
        const surfaces = parseSurfaces(doc);
        const alignments = parseAlignments(doc);
        const parcels = parseParcels(doc);
        const breaklines = parseBreaklines(doc);
        const { meshes: pipeMeshes, lines: pipeLines } = parsePipeNetworks(doc);
        if (cancelled) return;
        setSummary(summarize(surfaces, alignments, parcels, breaklines, pipeMeshes, pipeLines));
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(String(err));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  const sortedDiameters = useMemo(
    () => Array.from(summary.pipesByDiameter.entries()).sort((a, b) => parseInt(a[0]) - parseInt(b[0])),
    [summary.pipesByDiameter],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('landxmlQuantities.title')}</DialogTitle>
        <DialogDescription className='truncate'>{layerName}</DialogDescription>
      </DialogHeader>
      <div className='max-h-[60vh] overflow-auto flex flex-col gap-4 py-4'>
        {loading ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='size-4 animate-spin' />
            {t('landxmlQuantities.loading')}
          </div>
        ) : error ? (
          <div className='text-sm text-red-500'>{error}</div>
        ) : (
          <>
            <Section title={t('landxmlQuantities.surfaces')}>
              <Row label={t('landxmlQuantities.count')} value={String(summary.surfaceCount)} />
              <Row label={t('landxmlQuantities.totalArea')} value={`${summary.surfaceArea.toFixed(2)} m²`} />
            </Section>
            <Section title={t('landxmlQuantities.alignments')}>
              <Row label={t('landxmlQuantities.count')} value={String(summary.alignmentCount)} />
              <Row label={t('landxmlQuantities.totalLength')} value={`${summary.alignmentLength.toFixed(2)} m`} />
            </Section>
            <Section title={t('landxmlQuantities.parcels')}>
              <Row label={t('landxmlQuantities.count')} value={String(summary.parcelCount)} />
              <Row label={t('landxmlQuantities.totalArea')} value={`${summary.parcelArea.toFixed(2)} m²`} />
            </Section>
            <Section title={t('landxmlQuantities.breaklines')}>
              <Row label={t('landxmlQuantities.count')} value={String(summary.breaklineCount)} />
              <Row label={t('landxmlQuantities.totalLength')} value={`${summary.breaklineLength.toFixed(2)} m`} />
            </Section>
            <Section title={t('landxmlQuantities.pipes')}>
              <Row label={t('landxmlQuantities.count')} value={String(summary.pipeCount)} />
              <Row label={t('landxmlQuantities.totalLength')} value={`${summary.pipeLength.toFixed(2)} m`} />
              {sortedDiameters.length > 0 && (
                <div className='mt-1 flex flex-col border rounded-md text-xs'>
                  {sortedDiameters.map(([dia, { count, length }]) => (
                    <div key={dia} className='flex justify-between border-b last:border-b-0 px-3 py-1.5 font-mono'>
                      <span>{dia}</span>
                      <span>
                        {count} · {length.toFixed(2)} m
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
            <Section title={t('landxmlQuantities.structures')}>
              <Row label={t('landxmlQuantities.count')} value={String(summary.structureCount)} />
            </Section>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant='outline' onClick={() => dialog.dismissActiveDialog()}>
          {t('landxmlQuantities.close')}
        </Button>
      </DialogFooter>
    </>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className='flex flex-col gap-1'>
    <h3 className='text-xs font-medium text-muted-foreground'>{title}</h3>
    <div className='flex flex-col gap-0.5'>{children}</div>
  </section>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className='flex justify-between gap-2 text-sm font-mono'>
    <span className='text-muted-foreground'>{label}</span>
    <span>{value}</span>
  </div>
);
